import { useEffect, useState, useCallback, useRef } from "react";
import { analyzeErrorMessage } from "../utils/analyzeErrorMessage";
import { GraduationCap, Loader2, Trash2, LayoutDashboard, ChevronRight, ChevronDown, Check, Lightbulb, Info, ListChecks, FileText, History, Eye } from "lucide-react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { sessionService } from "../../application/SessionApplicationService";
import { completeSessionReview, listLiveInsights } from "../../application/sessionUseCases";
import { listSessionHighlights } from "../../application/sessionHighlights";
import { getSessionTemplate } from "../../application/sessionTemplates";
import { getPatient, getCouplePatient } from "../../application/sessionPatients";
import SessionViewBasic from "../../../../modules/session/ui/components/ViewDetails/SessionViewBasic";
import SessionToolDock from "../components/SessionToolDock";
import SessionPayments from "../components/SessionPayments";
import SessionWhiteboards from "../components/SessionWhiteboards";
import EditSessionModal from "../../../../modules/session/ui/components/ViewDetails/EditSessionModal";
import { Session } from "../../domain/Session";
import { ConstellationSnapshots, NewSessionFromBoard, useSessionConstellation } from "../../../constellation";
import ConstellationIcon from "../../../../shared/ui/icons/ConstellationIcon";
import SessionViewNotes from "../../../../modules/session/ui/components/ViewDetails/SessionViewNotes";
import SessionPreparation from "../components/ViewDetails/SessionPreparation";
import SessionAttendance from "../components/ViewDetails/SessionAttendance";
import SessionCourseModule from "../components/ViewDetails/SessionCourseModule";
import { useSessionCourse } from "../../application/useSessionCourse";
import { asksToBeMarkedHeld, isUpcoming } from "../../domain/sessionExtras";
import SessionHeldPrompt from "../components/ViewDetails/SessionHeldPrompt";
import SessionReviewBanner from "../../../../modules/session/ui/components/ViewDetails/SessionReviewBanner";
import EditSessionNotesModal from "../../../../modules/session/ui/components/ViewDetails/EditSessionNotesModal";
import Button from "../../../../shared/ui/button/Button";
import SessionViewAIAnalysisForm, { AISummary, TriggeredAlertsList } from "../../../../modules/session/ui/components/ViewDetails/SessionViewAIAnalysisForm";
import type { SessionTemplate } from "../../../session_templates";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import PageBreadcrumb from "../../../../shared/ui/common/PageBreadCrumb";
import { useSubscription } from "../../../subscription";
import { useAiInsightsGate } from "../../../ai_insights";
import { motion, AnimatePresence } from "framer-motion";
import { LiveInsightsDashboard } from "../../../../modules/session/ui/components/live-insights/LiveInsightsDashboard";
import SessionAgreementsTab from "../../../../modules/session/ui/components/ViewDetails/SessionAgreementsTab";
import SessionEventsView from "../components/SessionEvents/SessionEventsView";
import { EmdrSessionPanel, EmdrSessionSummary, useEmdrSets } from "../../../emdr";
import { subjectSessionsPath } from "../../domain/sessionsPath";
import { useAdoptedTools } from "../../../marketplace";
import SessionViewTranscription from "../../../../modules/session/ui/components/ViewDetails/SessionViewTranscription";
import { LiveInsight, LiveInsightMessage } from "../../../../types/live-insights";
import { SessionNoteHighlight } from "../../domain/Session";

export default function SessionDetails() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  // Finishing a review without an analysis: its own flag, so neither decision
  // can be taken while the other is in flight.
  const [finishingReview, setFinishingReview] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditNotesModalOpen, setIsEditNotesModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTemplate | null>(null);
  // `?tab=emdr` is where the EMDR board's Finish lands, on the sets it saved.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'overview' | 'course' | 'analysis' | 'insights' | 'agreements' | 'transcription' | 'events' | 'emdr' | 'constellation'>(
    requestedTab === 'emdr' || requestedTab === 'constellation' ? requestedTab : 'overview'
  );
  const { data: adoptedTools, isLoading: adoptedToolsLoading } = useAdoptedTools();
  const emdrAdopted = Boolean(adoptedTools?.some((tool) => tool.slug === "emdr"));
  /*
   * Tool tabs only where the tool left something in this session: EMDR sets
   * rated, a constellation board launched for it. A session that never used
   * either has nothing to show there.
   */
  const emdrSets = useEmdrSets(emdrAdopted && id ? id : null);
  const constellation = useSessionConstellation(id);

  // A tool tab asked for in the URL, or left open, that turns out to have
  // nothing in it goes back to the overview — once everything has answered.
  useEffect(() => {
    if (!session) return;
    if (activeTab === 'emdr' && !adoptedToolsLoading && !emdrSets.loading && emdrSets.sets.length === 0) {
      setActiveTab('overview');
    }
    if (
      activeTab === 'constellation' &&
      !constellation.loading &&
      !constellation.hasConstellation &&
      !session.constellation_notes?.trim()
    ) {
      setActiveTab('overview');
    }
  }, [activeTab, adoptedToolsLoading, constellation.hasConstellation, constellation.loading, emdrSets.loading, emdrSets.sets.length, session]);
  const [hasInsights, setHasInsights] = useState(false);
  const [historicalInsights, setHistoricalInsights] = useState<LiveInsightMessage[]>([]);
  const [highlights, setHighlights] = useState<SessionNoteHighlight[]>([]);
  const { isPremium } = useSubscription();
  const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
  const { breakpoint } = useBreakpoint();
  // A mentoring's course: read here, since it decides whether the tab exists.
  const sessionCourse = useSessionCourse(id ?? "");
  const hasCourse = Boolean(sessionCourse.course?.course);
  const isMobileView = ['mobile', 'sm', 'md', 'lg'].includes(breakpoint);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Every fetch gets a number; only the latest one may store its response,
  // so a slow poll or the previous session's fetch cannot overwrite it.
  const fetchSeqRef = useRef(0);
  const initialFetchSeqRef = useRef(0);

  // 🔹 Cargar la sesión
  const fetchSession = useCallback(async (isInitial = false) => {
    if (!id) return;
    const fetchSeq = ++fetchSeqRef.current;
    const initialFetchSeq = isInitial ? ++initialFetchSeqRef.current : 0;
    try {
      if (isInitial) setLoading(true);
      let sessionData = await sessionService.getSessionById(id, { log_view: isInitial });

      // Si no viene el nombre del paciente, lo buscamos
      if (!sessionData.patient_name || !sessionData.patient_last_name) {
        try {
          if (sessionData.type === 'couple' && sessionData.couple_patient_id) {
            const cp = await getCouplePatient(sessionData.couple_patient_id);
            const p1Name = cp.patient_1 ? `${cp.patient_1.first_name} ${cp.patient_1.last_name || ''}`.trim() : '';
            const p2Name = cp.patient_2 ? `${cp.patient_2.first_name} ${cp.patient_2.last_name || ''}`.trim() : '';

            sessionData = {
              ...sessionData,
              patient_name: p1Name && p2Name ? `${p1Name} & ${p2Name}` : cp.display_name,
              patient_last_name: "",
            };
          } else if (sessionData.patient_id) {
            const patient = await getPatient(sessionData.patient_id);
            sessionData = {
              ...sessionData,
              patient_name: patient.first_name,
              patient_last_name: patient.last_name,
            };
          }
        } catch (error) {
          console.error("Error fetching patient extra info:", error);
        }
      }

      if (fetchSeq === fetchSeqRef.current) setSession(sessionData);
    } catch (err) {
      console.error("Error fetching session:", err);
    } finally {
      if (isInitial && initialFetchSeq === initialFetchSeqRef.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSession(true);
  }, [fetchSession]);

  // Selected tab could be 'analysis' from a saved URL; fall back when it is hidden.
  useEffect(() => {
    if (!adoptionLoading && !aiInsightsAdopted && activeTab === 'analysis') {
      setActiveTab('overview');
    }
  }, [adoptionLoading, aiInsightsAdopted, activeTab]);

  // Poll if the session is processing
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (session?.status?.toLowerCase() === 'processing' || session?.status?.toLowerCase() === 'pending') {
      interval = setInterval(() => {
        fetchSession(false);
      }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [session?.status, fetchSession]);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!id || !isPremium) return;
      try {
        const insights = await listLiveInsights(id);
        if (insights && insights.length > 0) {
          // Synthesize 'type' field and flatten metadata for dashboard compatibility
          const formatted = insights.map((insight: LiveInsight) => ({
            ...insight,
            metadata: insight.metadata_json, // Add metadata property for components expecting it
            ...insight.metadata_json, // Keep flattened properties for backward compatibility
            type: (insight.metadata_json as { type?: string })?.type || 'ClinicalInsight'
          })) as LiveInsightMessage[];
          setHistoricalInsights(formatted);
          setHasInsights(true);
        } else {
          setHasInsights(false);
          setHistoricalInsights([]);
          if (activeTab === 'insights') {
            setActiveTab('overview');
          }
        }
      } catch (err) {
        console.error("Error fetching live insights:", err);
      }
    };
    fetchInsights();
  }, [id, isPremium, activeTab]);

  useEffect(() => {
    if (!id || !isPremium) return;
    listSessionHighlights(id).then(data => setHighlights(data)).catch(() => {});
  }, [id, isPremium]);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (session?.template_id) {
        try {
          const tpl = await getSessionTemplate(session.template_id);
          setSelectedTemplate(tpl);
        } catch (err) {
          console.error("Error fetching template:", err);
        }
      } else {
        setSelectedTemplate(null);
      }
    };
    fetchTemplate();
  }, [session?.template_id]);



  // 🔹 Analizar con IA
  const handleAnalyze = async () => {
    if (!session) return;
    setAnalyzeError(null);
    try {
      setAnalyzing(true);
      await sessionService.requestAnalysis(session.id);
      await fetchSession(false);
    } catch (err) {
      console.error("Error analyzing session:", err);
      setAnalyzeError(analyzeErrorMessage(err, t));
    } finally {
      setAnalyzing(false);
    }
  };

  // The therapist read the notes and asked for the analysis. They are shown
  // what they asked for: the analysis tab, where the result lands.
  const handleAnalyzeFromReview = async () => {
    await handleAnalyze();
    setActiveTab('analysis');
  };

  // The other answer: the notes say what the session said, and nothing needs
  // to go to a model. The session is finished as it is.
  const handleFinishReview = async () => {
    if (!session) return;
    setAnalyzeError(null);
    try {
      setFinishingReview(true);
      await completeSessionReview(session.id);
      await fetchSession(false);
    } catch (err) {
      console.error("Error finishing the session review:", err);
      setAnalyzeError(t("session.review.finishError", "Could not finish the session. Please try again."));
    } finally {
      setFinishingReview(false);
    }
  };

  // 🔹 Eliminar sesión
  const handleDelete = async () => {
    if (!id) return;
    const confirmed = window.confirm(t("session.deleteConfirm"));
    if (!confirmed) return;

    try {
      setDeleting(true);
      await sessionService.deleteSession(id);
      // Back to the sessions of the record it belonged to.
      navigate(subjectSessionsPath(session ?? {}), { replace: true });
    } catch (err) {
      console.error("Error deleting session:", err);
      alert(t("session.deleteErrorMsg"));
    } finally {
      setDeleting(false);
    }
  };

  // 🔹 Loading
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="animate-spin w-8 h-8 text-gray-500" />
      </div>
    );
  }

  // 🔹 No encontrada
  if (!session) {
    return <div className="p-6 text-center text-gray-500">{t("session.itemNotFound")}</div>;
  }

  // 🔹 Tabs Configuration
  const tabs: { id: string; label: string; icon: JSX.Element }[] = [
    { id: 'overview', label: t("common.overview"), icon: <LayoutDashboard className="w-4 h-4" /> },
  ];
  // A training centre's mentoring: its course and module get their own tab.
  if (hasCourse) {
    tabs.push({ id: 'course', label: t("session.course.tab"), icon: <GraduationCap className="w-4 h-4" /> });
  }
  if (session.transcription) {
    tabs.push({ id: 'transcription', label: t("session.transcription", "Transcription"), icon: <FileText className="w-4 h-4" /> });
  }
  // The analysis tab belongs to the AI Insights tool — hide it entirely when the
  // therapist has not adopted it.
  if (adoptionLoading || aiInsightsAdopted) {
    tabs.push({ id: 'analysis', label: t("session.insightAnalysis"), icon: <Lightbulb className="w-4 h-4" /> });
  }
  tabs.push(
    { id: 'agreements', label: t("session.agreements.title"), icon: <ListChecks className="w-4 h-4" /> },
  );
  if (isPremium && hasInsights) {
    tabs.push({ id: 'insights', label: "Live Insights", icon: <Lightbulb className="w-4 h-4" /> });
  }
  const hasConstellation = constellation.hasConstellation || Boolean(session.constellation_notes?.trim());
  if (hasConstellation) {
    tabs.push({ id: 'constellation', label: t("constellation.session.tab"), icon: <ConstellationIcon className="w-4 h-4" /> });
  }
  if (emdrSets.sets.length > 0) {
    tabs.push({ id: 'emdr', label: t("emdr.session.tab"), icon: <Eye className="w-4 h-4" /> });
  }
  // How the session got here: made, messaged, moved, settled.
  tabs.push({ id: 'events', label: t("session.events.tab"), icon: <History className="w-4 h-4" /> });

  // 🔹 Render principal
  return (
    <div>
      <PageMeta />
      <PageBreadcrumb pageTitle={`${session.title}`} />

      {/* Replaces the inline "Register payment" button that used to sit in the
          session header: the same action, in the dock every other tool uses. */}
      <SessionToolDock
        sessionId={session.id}
        patientId={session.patient_id}
        patientLabel={session.title}
      />

      {/* Who, when and where it stands: above every tab, full width. */}
      <div className="mb-6">
        <SessionViewBasic
          session={session}
          onEdit={() => setIsEditModalOpen(true)}
          onSessionChange={(changes) => setSession((current) => (current ? { ...current, ...changes } : current))}
        />
      </div>

      {/* Responsive tab navigation */}
      <div className="w-full mb-8 relative z-30">
        {isMobileView ? (
          /* 📱 Mobile/Tablet: dropdown selector */
          <div className="px-1 max-w-sm mx-auto">
            <div className="relative">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`
                  w-full h-12 flex items-center justify-between px-5 rounded-2xl border transition-all duration-300 shadow-theme-sm
                  ${isMobileMenuOpen
                    ? "bg-white dark:bg-brand-500 border-brand-500 ring-4 ring-brand-500/10"
                    : "bg-white/50 dark:bg-white/[0.03] border-gray-200 dark:border-white/5 backdrop-blur-md"}
                `}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-4 h-4 flex items-center justify-center ${isMobileMenuOpen ? "text-brand-600 dark:text-white" : "text-brand-500"}`}>
                    {tabs.find(t => t.id === activeTab)?.icon}
                  </span>
                  <span className={`text-sm font-bold ${isMobileMenuOpen ? "text-brand-600 dark:text-white" : "text-content-primary"}`}>
                    {tabs.find(t => t.id === activeTab)?.label}
                  </span>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMobileMenuOpen ? "rotate-180 text-brand-600 dark:text-white" : "text-content-tertiary"}`} />
              </button>

              <AnimatePresence>
                {isMobileMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setIsMobileMenuOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="absolute left-0 right-0 mt-2 z-50 bg-surface-raised border border-gray-100 dark:border-white/5 rounded-2xl shadow-theme-xl overflow-hidden backdrop-blur-sm"
                    >
                      <div className="p-1.5 space-y-1">
                        {tabs.map((tab) => (
                          <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id as typeof activeTab); setIsMobileMenuOpen(false); }}
                            className={`
                              w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
                              ${activeTab === tab.id
                                ? "bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 font-bold"
                                : "text-content-secondary hover:bg-gray-50 dark:hover:bg-white/5 font-medium"}
                            `}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-4 h-4 flex items-center justify-center ${activeTab === tab.id ? "text-brand-600 dark:text-brand-400" : "text-content-tertiary"}`}>
                                {tab.icon}
                              </span>
                              <span className="text-sm">{tab.label}</span>
                            </div>
                            {activeTab === tab.id && <Check className="w-4 h-4" />}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          /* 💻 Desktop: centered pill nav */
          <div className="flex justify-center overflow-x-auto no-scrollbar pb-2">
            <nav className="inline-flex p-1.5 bg-gray-100/50 dark:bg-white/[0.03] backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-2xl shadow-theme-xs" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`
                    relative flex items-center gap-2.5 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap
                    ${activeTab === tab.id
                      ? "text-brand-600 dark:text-brand-400"
                      : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"}
                  `}
                >
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="active-session-tab-pill"
                      className="absolute inset-0 bg-white dark:bg-white/[0.05] rounded-xl shadow-theme-sm"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10">{tab.icon}</span>
                  <span className="relative z-10">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Only when something really is coming. A `pending` session with
                no audio is a draft the therapist is writing themselves, and
                telling them it is queued leaves them waiting on a worker that
                was never given anything to do. */}
            {(session.status?.toLowerCase() === 'processing' ||
              (session.status?.toLowerCase() === 'pending' && Boolean(session.audio_url))) && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden p-6 rounded-3xl border border-accent/20 bg-accent-tint/30 bg-accent-tint shadow-theme-sm"
              >
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <Loader2 className="w-24 h-24 text-accent-text animate-spin" />
                </div>
                <div className="flex gap-5 items-start relative z-10">
                  <div className="flex-shrink-0 w-12 h-12 bg-accent-tint rounded-2xl flex items-center justify-center shadow-sm">
                    <Loader2 className="w-6 h-6 text-accent-text animate-spin" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-content-primary mb-1">
                      {session.status?.toLowerCase() === 'processing' 
                        ? t("session.audio.processingTitle", t("audio.processingTitle", "Audio is being processed")) 
                        : t("session.pending", "Waiting in queue...")}
                    </h3>
                    <p className="text-content-tertiary max-w-2xl leading-relaxed">
                      {t("session.processingWarning")}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* The review asks the therapist a question, and both answers
                are theirs: run the analysis, or finish with the notes as
                they are. Nothing else on the page explains why a transcribed
                session carries no analysis yet. */}
            {/* A booked session that has not happened yet: the page is for
                preparing it, and the notes come afterwards. */}
            {(session.session_status ?? 'pending') === 'pending' && !session.audio_url && isUpcoming(session.date) && (
              <div className="flex items-start gap-3 p-5 rounded-2xl border border-brand-200 dark:border-brand-500/30 bg-brand-50/60 dark:bg-brand-500/10">
                <Info className="w-5 h-5 text-brand-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-content-primary">{t("session.upcoming.title")}</p>
                  <p className="text-sm text-content-secondary leading-relaxed">{t("session.upcoming.description")}</p>
                </div>
              </div>
            )}

            {/* Its time has come and it has notes, but only the therapist says
                it took place: notes may have been written ahead. */}
            {asksToBeMarkedHeld(session) && (
              <SessionHeldPrompt
                sessionId={session.id}
                onChange={(changes) => setSession((current) => (current ? { ...current, ...changes } : current))}
              />
            )}

            {session.status?.toLowerCase() === 'review' && (
              <SessionReviewBanner
                onAnalyze={handleAnalyzeFromReview}
                onFinishWithoutAnalysis={handleFinishReview}
                analyzing={analyzing}
                finishing={finishingReview}
                canAnalyze={adoptionLoading || aiInsightsAdopted}
                error={analyzeError}
              />
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 xl:gap-8">
              {/* Beside the notes on a wide screen, after them on a phone:
                  what concerns the people in it rather than what was said. */}
              {(session.group_patient_id || (session.analysis?.triggered_alerts?.length ?? 0) > 0) && (
                <div className="order-2 xl:order-1 xl:col-span-4 space-y-6">
                  {session.group_patient_id && <SessionAttendance sessionId={session.id} />}

                  {session.analysis?.triggered_alerts && session.analysis.triggered_alerts.length > 0 && (
                    <TriggeredAlertsList data={session.analysis.triggered_alerts} />
                  )}
                </div>
              )}

              {/* Main column: preparation, notes and what came of them. */}
              <div
                className={`order-1 xl:order-2 space-y-6 ${
                  session.group_patient_id || (session.analysis?.triggered_alerts?.length ?? 0) > 0
                    ? "xl:col-span-8"
                    : "xl:col-span-12"
                }`}
              >
                {/* Above the notes on purpose: "did I bill for this?" is
                    answered at a glance, without displacing what the page is
                    for. Renders nothing when the session was never charged. */}
                <SessionPayments sessionId={session.id} />

                <SessionPreparation
                  key={session.id}
                  sessionId={session.id}
                  preparation={session.preparation ?? ""}
                  onSaved={(preparation) => setSession((current) => (current ? { ...current, preparation } : current))}
                />

                {/* The EMDR work at a glance once there is a course to show —
                    two sets or more; every set is on the EMDR tab. */}
                {emdrSets.sets.length >= 2 && (
                  <EmdrSessionSummary sessionId={session.id} onShowSets={() => setActiveTab('emdr')} />
                )}

                <SessionViewNotes
                  notes={session.notes || ""}
                  onEdit={() => setIsEditNotesModalOpen(true)}
                  // A session opened ahead has no notes yet: record them,
                  // upload them or photograph them through the new-session
                  // steps, on this same session. Not while it is processing.
                  onAddNotes={
                    session.status === "pending" && !session.audio_url
                      ? (mode) => navigate("/sessions/new", { state: { sessionId: session.id, sessionType: mode } })
                      : undefined
                  }
                  aiAvailable={aiInsightsAdopted}
                  template={selectedTemplate}
                  analysis={session.analysis}
                  sessionId={session.id}
                  patientId={session.patient_id || session.couple_patient_id || session.group_patient_id}
                  isPremium={isPremium}
                  highlights={highlights}
                  onHighlightCreate={(h) => setHighlights(prev => [...prev, h])}
                  onHighlightDelete={(id) => setHighlights(prev => prev.filter(h => h.id !== id))}
                />

                {/* Boards drawn together during the call, kept only where the
                    therapist chose to keep them. */}
                <SessionWhiteboards sessionId={session.id} />

                {session.analysis_skipped_reason === "analysis_declined" && !session.analysis && (
                  <div className="flex items-start gap-3 p-4 rounded-xl border border-border-subtle bg-surface-sunken">
                    <Info className="w-4 h-4 text-content-tertiary mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-content-secondary leading-relaxed">
                      {t("session.analysisDeclined")}
                    </p>
                  </div>
                )}

                {session.analysis_skipped_reason === "notes_too_short" && (
                  <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50/60 dark:bg-amber-900/10">
                    <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
                      {t("session.analysisSkippedNotesTooShort")}
                    </p>
                  </div>
                )}

                {session.analysis && (
                  <div className="space-y-4">
                    <AISummary
                      main_topic={session.analysis.main_topic}
                      emotional_status={session.analysis.emotional_status}
                      relevant_behaviors={session.analysis.relevant_behaviors}
                      therapeutic_advances={session.analysis.therapeutic_advances}
                      risks={session.analysis.risks}
                      agreements={session.analysis.agreements}
                    />
                    {(adoptionLoading || aiInsightsAdopted) && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => setActiveTab('analysis')}
                          className="py-3 px-6 rounded-xl border border-brand-200 bg-brand-50/50 text-brand-600 text-sm font-bold hover:bg-brand-100 transition-all flex items-center gap-2"
                        >
                          {t("session.viewFullAnalysis")}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t mt-4">
              {/* Icon goes through `startIcon`, not into the children: the
                  label span is inline and preflight renders <svg> as a block,
                  which pushed the icon onto its own line. */}
              <Button
                variant="outline"
                className="w-full max-w-[200px] text-red-500 hover:text-red-600 hover:bg-red-50 border-gray-200"
                onClick={handleDelete}
                disabled={analyzing}
                loading={deleting}
                startIcon={<Trash2 className="w-4 h-4" />}
              >
                {deleting ? t("session.deleting") : t("session.deleteAction")}
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className="col-span-full">
            <SessionViewAIAnalysisForm
              analysis={session.analysis}
              analyzing={analyzing}
              handleAnalyze={handleAnalyze}
              analysisSkippedReason={session.analysis_skipped_reason}
              analyzeError={analyzeError}
              locked={!adoptionLoading && !aiInsightsAdopted}
            />
          </div>
        )}

        {activeTab === 'course' && hasCourse && (
          <SessionCourseModule course={sessionCourse} />
        )}

        {activeTab === 'events' && (
          <section
            aria-labelledby="session-events-title"
            className="col-span-full glass-effect glass-border border rounded-2xl p-6 shadow-theme-sm"
          >
            <h3 id="session-events-title" className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
              {t("session.events.title")}
            </h3>
            <SessionEventsView sessionId={session.id} />
          </section>
        )}

        {activeTab === 'constellation' && (
          <div className="col-span-full space-y-8">
            {session.constellation_notes && session.constellation_notes.trim() && (
              <div className="glass-effect glass-border border rounded-2xl p-6 shadow-theme-sm">
                <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {t("session.constellationNotes", "Constellation notes")}
                </h3>
                <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  {session.constellation_notes}
                </p>
              </div>
            )}

            <ConstellationSnapshots sessionId={session.id} />

            {/* Continue longitudinal work: start a new session seeded from
                this session's final constellation board (issue #197). */}
            <NewSessionFromBoard sessionId={session.id} />
          </div>
        )}

        {activeTab === 'emdr' && (
          <section
            aria-labelledby="session-emdr-title"
            className="col-span-full glass-effect glass-border border rounded-2xl p-6 shadow-theme-sm"
          >
            <h3 id="session-emdr-title" className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
              {t("emdr.session.title")}
            </h3>
            <EmdrSessionPanel sessionId={session.id} />
          </section>
        )}

        {activeTab === 'agreements' && (
          <div className="col-span-full py-4">
            <SessionAgreementsTab sessionId={session.id} />
          </div>
        )}

        {activeTab === 'transcription' && (
          <div className="col-span-full">
            <SessionViewTranscription
              transcription={session.transcription || ""}
              sessionId={session.id}
              sessionType={session.type}
            />
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="col-span-full">
            <LiveInsightsDashboard messages={historicalInsights} connectionState="idle" fullPage={true} />
          </div>
        )}

        {isEditModalOpen && (
          <EditSessionModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            session={session}
            onSuccess={fetchSession}
          />
        )}

        {isEditNotesModalOpen && (
          <EditSessionNotesModal
            isOpen={isEditNotesModalOpen}
            onClose={() => setIsEditNotesModalOpen(false)}
            session={session}
            onSuccess={fetchSession}
            template={selectedTemplate}
          />
        )}

      </div>
    </div>
  );
}
